import { PrestoConfig } from '../../../../interfaces';

// takes our saved options and transforms them into a format that plyr.io will use
export default function ({
  preset = {},
  chapters,
  branding,
  src,
  analytics,
  automations,
  autoplay,
  blockAttributes = {},
  provider = '',
  provider_video_id = '',
  youtube,
  type,
  isAdmin,
  i18n,
  storage = {
    enabled: !isAdmin,
    key: `presto-player-${preset.id}`,
  },
}: PrestoConfig) {
  const controlOptions = ['play-large', 'rewind', 'play', 'fast-forward', 'progress', 'current-time', 'mute', 'volume', 'captions', 'settings', 'pip', 'fullscreen'];
  const required = ['settings'];
  const disabled = [...(['youtube', 'vimeo'].includes(provider) ? ['pip'] : [])];

  const controls = controlOptions.filter(key => {
    return (preset?.[key] || required.includes(key)) && !disabled.includes(key);
  });

  let playerSettings = {
    id: blockAttributes?.id,
    title: blockAttributes?.title || '',
    blockAttributes: {
      ...blockAttributes,
      ...{ type },
    },
    src,
    ajaxProgress: true,
    analytics,
    preset,
    automations,
    mutedPreview: {
      enabled: blockAttributes?.mutedPreview?.enabled,
    },
    loop: {
      active: preset?.on_video_end === 'loop' ? true : false,
    },
    chapters,
    controls,
    settings: ['captions', 'quality', 'loop', ...(preset?.speed ? ['speed'] : [])],
    hideControls: preset?.auto_hide,
    captions: { active: preset?.captions_enabled, language: 'auto', update: false },
    logo: branding?.logo,
    logo_width: branding?.logo_width,
    hide_logo: preset?.hide_logo,
    lazy_load_youtube: preset?.lazy_load_youtube,
    save_player_position: !!preset?.save_player_position,
    sticky_scroll: preset?.sticky_scroll,
    play_video_viewport: preset?.play_video_viewport,
    autoplay: autoplay && !window.wp?.blocks ? true : false,
    // playsinline: !!block_attributes?.playsInline,
    ...(provider ? { provider } : {}),
    ...(provider_video_id ? { provider_video_id } : {}),
    ...(blockAttributes?.poster ? { poster: blockAttributes.poster } : {}),
    invertTime: !preset?.show_time_elapsed,
    // debug: true,
    storage,
    keyboard: { focused: false, global: false }, // this is required so email collection doesn't trigger shortcuts.
    resetOnEnd: !!preset?.reset_on_end,
    vimeo: {
      byline: false,
      portrait: false,
      title: false,
      speed: true,
      transparent: false,
      // Custom settings from Plyr
      customControls: true,
      // Whether the owner of the video has a Pro or Business account
      // (which allows us to properly hide controls without CSS hacks, etc)
      premium: false,
      playsinline: !!blockAttributes?.playsInline,
      // Prevent Vimeo blocking site
    },
    youtube: {
      rel: 0, // No related vids
      showinfo: 0, // Hide info
      iv_load_policy: 3, // Hide annotations
      modestbranding: 1, // Hide logos as much as possible (they still show one in the corner when paused)
      // Custom settings from Plyr
      customControls: true,
      noCookie: !!youtube?.noCookie, // Whether to use an alternative version of YouTube without cookies
      playsinline: !!blockAttributes?.playsInline,
    },
    tooltips: {
      controls: true,
      seek: true,
    },
    // Localisation
    i18n,
    ...(blockAttributes?.ratio !== "original" && { ratio: blockAttributes?.ratio }),
  };

  if (window?.wp?.hooks) {
    playerSettings = window.wp.hooks.applyFilters('presto.playerSettings', playerSettings);
  }

  return playerSettings;
}
